import React, { Component } from 'react'
import { observer } from 'mobx-react'
import { action } from 'mobx'
import PropTypes from 'prop-types'
import { Field } from 'formik'
import get from 'lodash/get'
import moment from 'moment'

import {
  getPositionSuggestions,
  getCompanySuggestions,
  getPlaceSuggestions
} from '@atheneum/api'
import { stringifyDate } from '@atheneum/api/dist/lib'

import Action from '@components/Action'
import Button from '@components/Button'
import Checkbox from '@components/Checkbox'
import { Autocomplete, Typeahead } from '@components/Form/Autocomplete'
import RichTextEditor from '@components/Form/RichTextEditor'
import DeleteIcon from '@components/Icon/Delete'
import MonthYearPickerRange from '@components/MonthPicker/MonthYearPickerRange'

class ExpertExperienceSection extends Component {
  static propTypes = {
    form: PropTypes.object,
    index: PropTypes.number,
    experience: PropTypes.object
  }

  renderTitle = index => {
    return index > 0 ? `Work experience #${index + 1}` : `Work experience`
  }

  renderDeleteAction = index => {
    if (index === 0) return null

    return (
      <Action right onClick={() => this.onDelete(index)}>
        <DeleteIcon blue />
      </Action>
    )
  }

  onDelete = action(index => {
    const { form } = this.props
    const deletedWorkExperience = form.values.experiences.splice(index, 1)
    form.setFieldValue('experience', deletedWorkExperience, false)
  })

  handlePositionAutocomplete = input => {
    if (!input) return Promise.resolve([])
    const limit = 8
    return getPositionSuggestions(input, limit)
      .then(positions => {
        return positions
      })
      .catch(err => {
        console.error(err)
      })
  }

  handleCompanyAutocomplete = input => {
    if (!input) return Promise.resolve([])
    return getCompanySuggestions(input)
      .then(companies => {
        return companies
      })
      .catch(err => {
        console.error(err)
      })
  }

  handleLocationAutocomplete = (input, type) => {
    if (!input) return Promise.resolve([])
    return getPlaceSuggestions(input, type).then(res => {
      if (res.error) {
        return ''
      } else {
        this.setState({ suggestions: res.data })
        return res.data.map(place => place && place.description)
      }
    })
  }

  handleLocationChange = (location, index) => {
    const { form } = this.props
    if (!location) {
      form.setFieldValue(`experiences[${index}].location`, '', false)
    } else {
      form.setFieldValue(`experiences[${index}].location`, location, false)
    }
  }

  fromDateChanged = action((monthAndYear, index) => {
    let { form } = this.props
    const selectedMonthAndYear = moment(monthAndYear).toISOString()
    form.setFieldValue(
      `experiences[${index}].startDate`,
      stringifyDate(selectedMonthAndYear),
      false
    )
    form.setFieldTouched(`experiences[${index}].startDate`, true)
  })

  toDateChanged = action((monthAndYear, index) => {
    let { form } = this.props
    const selectedMonthAndYear = moment(monthAndYear).toISOString()
    form.setFieldValue(
      `experiences[${index}].endDate`,
      stringifyDate(selectedMonthAndYear),
      false
    )
    form.setFieldValue(`experiences[${index}].current`, false, false)
    form.setFieldTouched(`experiences[${index}].endDate`, true)
  })

  onCurrentChange = action((value, index) => {
    let { form } = this.props
    form.setFieldValue(`experiences[${index}].current`, value, false)
    if (value) {
      form.setFieldValue(`experiences[${index}].endDate`, null, false)
    }

    form.setFieldTouched(`experiences[${index}].current`, true)
    form.setFieldTouched(`experiences[${index}].endDate`, true)
  })

  render () {
    const { form, index, experience } = this.props

    const startDateFieldError =
      get(form.touched, `experiences[${index}].startDate`) &&
      get(form.errors, `experiences[${index}].startDate`)
    const endDateFieldError =
      get(form.touched, `experiences[${index}].endDate`) &&
      get(form.errors, `experiences[${index}].endDate`)

    return (
      <div className='expert-form__section'>
        <h2 className='expert-form__subtitle'>
          {this.renderTitle(index)}
          {this.renderDeleteAction(index)}
        </h2>
        <Field
          creatable
          data-cy='work-experience-position-autocomplete'
          itemFromCreatable={item => ({ name: item, id: item })}
          name={`experiences[${index}].position`}
          label={'Position *'}
          placeholder=''
          itemToString={item => item && item.name}
          loadOptions={this.handlePositionAutocomplete}
          component={Autocomplete}
        />
        <Field
          creatable
          data-cy='work-experience-company-autocomplete'
          itemFromCreatable={item => ({ name: item, id: item })}
          name={`experiences[${index}].company`}
          label={'Company *'}
          placeholder=''
          itemToString={item => item && item.name}
          loadOptions={this.handleCompanyAutocomplete}
          component={Autocomplete}
        />
        <Field
          name={`experiences[${index}].location`}
          label='Location'
          loadOptions={input =>
            this.handleLocationAutocomplete(input, '(cities)')
          }
          onInputValueChange={value => this.handleLocationChange(value, index)}
          component={Typeahead}
        />
        <div className='expert-form__input-group'>
          <label className='expert-form__label'>{'From-to *'}</label>
          <div
            className='expert-form__input-wrapper expert-form__input-wrapper--inline'
            data-cy='from-to-datepicker'
          >
            <MonthYearPickerRange
              fromError={startDateFieldError}
              toError={endDateFieldError}
              monthYearFrom={experience.startDate}
              monthYearTo={experience.endDate}
              onFromChange={value => this.fromDateChanged(value, index)}
              onToChange={value => this.toDateChanged(value, index)}
              onFromBlur={() =>
                form.setFieldTouched(`experiences[${index}].startDate`, true)
              }
              onToBlur={() =>
                form.setFieldTouched(`experiences[${index}].endDate`, true)
              }
            />
            <Checkbox
              className='m-l-xl'
              value={experience.current}
              label='Current'
              onChange={value => this.onCurrentChange(value, index)}
              onBlur={() =>
                form.setFieldTouched(`experiences[${index}].current`, true)
              }
            />
            <div className='expert-form__input-wrapper expert-form__month-range'>
              <div className='expert-form__input-error start-date'>
                {startDateFieldError}
              </div>
              <div className='expert-form__input-error end-date'>
                {endDateFieldError}
              </div>
            </div>
          </div>
        </div>
        <Field
          name={`experiences[${index}].description`}
          label='Description'
          placeholder='Type something'
          toolbarRight
          component={RichTextEditor}
        />
      </div>
    )
  }
}

class AddNewExperience extends Component {
  static propTypes = {
    form: PropTypes.any
  }

  onAdd = action(() => {
    let { form } = this.props
    const emptyWorkExperienceObject = {
      position: '',
      company: '',
      location: '',
      startDate: null,
      endDate: null,
      current: false,
      description: ''
    }
    const addedWorkExperience = form.values.experiences.concat(
      emptyWorkExperienceObject
    )
    form.setFieldValue('experiences', addedWorkExperience, false)
  })

  render () {
    const { form } = this.props
    return (
      <div className='expert-form__section'>
        {form.errors.experiences &&
          typeof form.errors.experiences === 'string' && (
            <div
              className='expert-form__input-error'
              style={{ marginBottom: 8 }}
            >
              {form.errors.experiences}
            </div>
          )}
        <div className='expert-form__input-group'>
          <label className='expert-form__label'>
            <Button secondary onClick={this.onAdd}>
              Add experience
            </Button>
          </label>
          <div className='expert-form__input-wrapper'>
            Expert's work experiences will automatically be organised
            chronologically by start-end year
          </div>
        </div>
      </div>
    )
  }
}

const ExpertExperienceForm = ({ form }) => {
  const experiences = form.values.experiences.sort((a, b) => moment(b.startDate).diff(a.startDate)).map((experience, index) => {
    return (
      <ExpertExperienceSection
        key={`${experience.id}-${index}-${experience.position}`}
        form={form}
        index={index}
        experience={experience}
      />
    )
  })
  return (
    <div>
      {experiences}
      <AddNewExperience form={form} />
    </div>
  )
}

ExpertExperienceForm.propTypes = {
  form: PropTypes.object
}

export default observer(ExpertExperienceForm)
